<template>
  <div>
    <div class="position-relative">
      <div
        class="iia-image position-relative mb-space-sm mb-md-space-lg"
        :data-background-image="dataBgImage"
        :class="{ 'iia-lazy': bgImageLazy }"
        :style="imageStyle">
        <div
          :style="gradientStyle"
          class="iia-gradient position-absolute w-100 p-space-sm p-lg-space-md"></div>
        <div
          class="position-absolute submit-btn-div w-100 d-flex justify-content-center">
          <button
            :class="ctaClass"
            class="mb-space-xs mb-lg-space-lg btn"
            @click="handleModalOpen">
            {{ ctaText }}
          </button>
        </div>
      </div>

      <div :class="`bg-${lineVariant}`" class="iia-line"></div>
    </div>

    <rds-modal
      @hidden="handleModalClose($event)"
      :visible="showModal"
      exterior-close-btn
      :size="modalSize"
      bg-variant="transparent"
      class="py-space-lg py-md-0">
      <div>
        <img :src="imageSource" :alt="imageAlt" class="ica-image" />
      </div>

      <template #footer>
        <div class="w-100 text-center d-block d-lg-none">
          <button
            :class="footerCtaClass"
            class="btn"
            aria-label="close"
            @click="handleButtonClick($event)">
            {{ footerCtaText }}
          </button>
        </div>
      </template>
    </rds-modal>
  </div>
</template>

<script setup lang="ts">
import { RdsModal } from "@rds-vue-ui/rds-modal";
import lozad from "lozad";
import { computed, onMounted, ref } from "vue";

interface Props {
  bgImageLazy?: boolean;
  imageSource: string;
  imageAlt: string;
  displayGradient?: boolean;
  gradientOpacity?: number;
  ctaText?: string;
  ctaTextSize?: string;
  ctaTextVariant?: string;
  ctaButtonVariant?: string;
  lineVariant?: string;
  modalSize?: "sm" | "md" | "lg" | "xl";
  footerCtaText?: string;
  footerCtaTextVariant?: string;
  footerCtaTextSize?: string;
  footerCtaVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  bgImageLazy: true,
  displayGradient: true,
  gradientOpacity: 0.85,
  ctaText: "Click to view the infographic",
  ctaTextSize: "md",
  ctaTextVariant: "dark-3",
  ctaButtonVariant: "secondary",
  lineVariant: "secondary",
  modalSize: "lg",
  footerCtaText: "Back to page",
  footerCtaTextVariant: "dark-3",
  footerCtaTextSize: "md",
  footerCtaVariant: "secondary",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when the footer cta is clicked.
   */
  (e: "buttonClick", event: Event): void;
  /**
   *  Emitted when the modal is closed.
   */
  (e: "modalClose", event: Event): void;
  /**
   *  Emitted when the modal is opened.
   */
  (e: "modalOpen", event: Event): void;
}>();

const showModal = ref(false);

const ctaClass = computed(() => {
  return `fs-${props.ctaTextSize} text-${props.ctaTextVariant} btn-${props.ctaButtonVariant}`;
});

const imageStyle = computed(() => {
  if (!props.bgImageLazy) {
    return { "background-image": `url('${props.imageSource}')` };
  } else {
    return null;
  }
});

const dataBgImage = computed(() => {
  return props.bgImageLazy ? props.imageSource : null;
});

const gradientStyle = computed(() => {
  if (props.displayGradient) {
    const color1 = "rgba(0,0,0,0)";
    const color2 =
      "rgba(0,0,0," + (0.745 * props.gradientOpacity).toString() + ")";
    const color3 = "rgba(0,0,0," + props.gradientOpacity.toString() + ")";

    return {
      background: `transparent linear-gradient(180deg, ${color1} 0%, ${color2} 28%, ${color3} 100%) 0% 0% no-repeat padding-box`,
    };
  } else {
    return null;
  }
});

const footerCtaClass = computed(() => {
  return `fs-${props.footerCtaTextSize} text-${props.footerCtaTextVariant} btn-${props.footerCtaVariant}`;
});

const handleModalOpen = (event: Event) => {
  emits("modalOpen", event);
  showModal.value = true;
};
const handleModalClose = (event: Event) => {
  emits("modalClose", event);
  showModal.value = false;
};
const handleButtonClick = (event: Event) => {
  emits("buttonClick", event);
  showModal.value = false;
};

onMounted(() => {
  if (props.bgImageLazy) {
    const observer = lozad(".iia-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.iia-image {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top;
  min-height: 450px;
}

.iia-gradient {
  bottom: 0px;
  min-height: 100px;
}

.ica-image {
  width: 100%;
  object-fit: cover;
  object-position: top;
}

.iia-line {
  height: 4px;
  width: 100%;
}
.submit-btn-div {
  bottom: 0;
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .iia-image {
    min-height: 700px;
    width: 540px;
  }
  .iia-line {
    width: 540px;
  }
  .iia-gradient {
    min-height: 223px;
  }
}
</style>
